import { useQuery } from "@tanstack/react-query";

import { initUseQuery, queryKey } from "simplydo/core/query";
import type { OpenAPI } from "simplydo/interfaces";

import api from "api";

type CreatedMessageThread = OpenAPI.POST<"/messages/threads">["response"];
type MessageThreadsResponse = OpenAPI.GET<"/messages/threads">["response"];

// The message tray always shows the first, unfiltered page of threads
const trayParams = (): [{ page: number; query: string }] => [
  { page: 1, query: "" },
];

export const useMessageThreads = (enabled: boolean) => {
  const { data } = useQuery(
    initUseQuery(api.messages.getThreads, trayParams(), { isEnabled: enabled }),
  );
  return {
    messageThreads: data?.messageThreads ?? [],
    newMessageCount: data?.newMessageCount ?? 0,
  };
};

export const getMessageThreads = (): MessageThreadsResponse["messageThreads"] =>
  api.queryClient.getQueryData<MessageThreadsResponse>(
    queryKey(api.messages.getThreads, trayParams()),
  )?.messageThreads ?? [];

export const refetchMessageThreads = () => {
  api.invalidateQueries(api.messages.getThreads, trayParams());
};

const updateMessageThreads = (
  updater: (data: MessageThreadsResponse) => MessageThreadsResponse,
) => {
  api.setQueryData(api.messages.getThreads, trayParams(), updater, {
    exact: true,
  });
};

export const messageThreadsCache = {
  markAsSeen(
    threadIds: string[],
    markAllAsSeen: boolean,
    userId: string,
    newMessageCount: number,
  ) {
    updateMessageThreads((data) => ({
      ...data,
      messageThreads: data.messageThreads.map((thread) =>
        markAllAsSeen || threadIds.includes(thread._id)
          ? { ...thread, seenBy: [...(thread.seenBy ?? []), userId] }
          : thread,
      ),
      newMessageCount,
    }));
  },
  onThreadCreated(newThreadId: string, thread: CreatedMessageThread) {
    updateMessageThreads((data) => {
      const index = data.messageThreads.findIndex((t) => t._id === newThreadId);
      const messageThreads = [...data.messageThreads];
      // A new thread's only users are its participants
      const listThread = { ...thread, allUsers: thread.participantUsers ?? [] };
      if (index !== -1) messageThreads[index] = listThread;
      else messageThreads.push(listThread);
      return { ...data, messageThreads };
    });
  },
};
